/**
 * Admin Costs Dashboard
 * Secret route for viewing per-unit costs of chat and insurance features
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Refresh as RefreshIcon, Sync as SyncIcon } from '@mui/icons-material';
import NavigationBar from '../components/NavigationBar';
import CostSummaryCard from '../components/admin/CostSummaryCard';
import ChatCostsTab from '../components/admin/ChatCostsTab';
import InsuranceCostsTab from '../components/admin/InsuranceCostsTab';
import { copilotApi } from '../services/copilotApi';
import type { ChatCostsResponse, InsuranceCostsResponse, CostSummaryResponse } from '../types/admin';

interface AdminCostsPageProps {
  signOut: () => void;
}

const AdminCostsPage: React.FC<AdminCostsPageProps> = ({ signOut }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const [summary, setSummary] = useState<CostSummaryResponse | null>(null);
  const [chatCosts, setChatCosts] = useState<ChatCostsResponse | null>(null);
  const [insuranceCosts, setInsuranceCosts] = useState<InsuranceCostsResponse | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, chatData, insuranceData] = await Promise.all([
        copilotApi.getAdminCostSummary(),
        copilotApi.getAdminChatCosts(),
        copilotApi.getAdminInsuranceCosts(),
      ]);
      setSummary(summaryData);
      setChatCosts(chatData);
      setInsuranceCosts(insuranceData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load cost data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSyncPricing = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await copilotApi.syncAdminPricing();
      if (result.success) {
        const parts: string[] = [];
        if (result.updated.length > 0) parts.push(`Updated: ${result.updated.length} models`);
        if (result.unchanged.length > 0) parts.push(`Unchanged: ${result.unchanged.length} models`);
        if (result.errors.length > 0) parts.push(`Errors: ${result.errors.length}`);
        setSyncMessage(parts.join(' | '));
        // Reload data to reflect new pricing
        await loadData();
      } else {
        setSyncMessage('Pricing sync failed');
      }
    } catch (err) {
      setSyncMessage(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <NavigationBar onSignOut={signOut} />
      <Box sx={{ flexGrow: 1, pt: '64px', overflow: 'auto' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight="bold">
                Cost Dashboard
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={handleSyncPricing}
                  disabled={syncing}
                >
                  Sync Pricing
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadData}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {/* Sync message */}
            {syncMessage && (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSyncMessage(null)}>
                {syncMessage}
              </Alert>
            )}

            {/* Error state */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Loading state */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Summary cards */}
                {summary && <CostSummaryCard data={summary} />}

                {/* Tabs */}
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                  <Tab label="Chat Costs" />
                  <Tab label="Insurance Costs" />
                </Tabs>

                {activeTab === 0 && chatCosts && <ChatCostsTab data={chatCosts} />}
                {activeTab === 1 && insuranceCosts && <InsuranceCostsTab data={insuranceCosts} />}
              </>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default AdminCostsPage;
